import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useGridVisibleRows } from '../../utils/useGridVisibleRows';
import { useGridApiMethod } from '../../utils/useGridApiMethod';
import { useGridSelector } from '../../utils/useGridSelector';
import { gridDensityRowHeightSelector, gridDensityFactorSelector } from '../density/densitySelector';
import { gridFilterStateSelector } from '../filter/gridFilterSelector';
import { gridPaginationSelector } from '../pagination/gridPaginationSelector';
import { gridSortingStateSelector } from '../sorting/gridSortingSelector';
import { useGridRegisterPipeApplier } from '../../core/pipeProcessing';
export const rowsMetaStateInitializer = state => _extends({}, state, {
  rowsMeta: {
    currentPageTotalHeight: 0,
    positions: []
  }
});
/**
 * @requires useGridPageSize (method)
 * @requires useGridPage (method)
 */

export const useGridRowsMeta = (apiRef, props) => {
  const {
    getRowHeight: getRowHeightProp,
    getRowSpacing,
    getEstimatedRowHeight
  } = props;
  const rowsHeightLookup = React.useRef({}); // Inspired by https://github.com/bvaughn/react-virtualized/blob/master/source/Grid/utils/CellSizeAndPositionManager.js

  const lastMeasuredRowIndex = React.useRef(-1);
  const hasRowWithAutoHeight = React.useRef(false);
  const rowHeightFromDensity = useGridSelector(apiRef, gridDensityRowHeightSelector);
  const filterState = useGridSelector(apiRef, gridFilterStateSelector);
  const paginationState = useGridSelector(apiRef, gridPaginationSelector);
  const sortingState = useGridSelector(apiRef, gridSortingStateSelector);
  const currentPage = useGridVisibleRows(apiRef, props);
  const hydrateRowsMeta = React.useCallback(() => {
    hasRowWithAutoHeight.current = false;
    apiRef.current.setState(state => {
      const densityFactor = gridDensityFactorSelector(state, apiRef.current.instanceId);
      const positions = [];
      const currentPageTotalHeight = currentPage.rows.reduce((acc, row) => {
        positions.push(acc);

        if (!rowsHeightLookup.current[row.id]) {
          rowsHeightLookup.current[row.id] = {
            sizes: {
              base: rowHeightFromDensity
            },
            isResized: false,
            autoHeight: false,
            needsFirstMeasurement: true // Assume all rows will need to be measured by default

          };
        }

        const {
          isResized,
          needsFirstMeasurement,
          sizes
        } = rowsHeightLookup.current[row.id];
        let baseRowHeight = rowHeightFromDensity;
        const existingBaseRowHeight = sizes.base;

        if (isResized) {
          // Do not recalculate resized row height and use the value from the lookup
          baseRowHeight = existingBaseRowHeight;
        } else if (getRowHeightProp) {
          const rowHeightFromUser = getRowHeightProp(_extends({}, row, {
            densityFactor
          }));

          if (rowHeightFromUser === 'auto') {
            if (needsFirstMeasurement) {
              const estimatedRowHeight = getEstimatedRowHeight ? getEstimatedRowHeight(_extends({}, row, {
                densityFactor
              })) : rowHeightFromDensity; // If the row was not measured yet use the estimated row height

              baseRowHeight = estimatedRowHeight != null ? estimatedRowHeight : rowHeightFromDensity;
            } else {
              baseRowHeight = existingBaseRowHeight;
            }

            hasRowWithAutoHeight.current = true;
            rowsHeightLookup.current[row.id].autoHeight = true;
          } else {
            // Default back to base rowHeight if getRowHeight returns null or undefined.
            baseRowHeight = rowHeightFromUser != null ? rowHeightFromUser : rowHeightFromDensity;
            rowsHeightLookup.current[row.id].needsFirstMeasurement = false;
            rowsHeightLookup.current[row.id].autoHeight = false;
          }
        } else {
          rowsHeightLookup.current[row.id].needsFirstMeasurement = false;
        } // We use an object to make simple to check if a height is already added or not


        const initialHeights = {
          base: baseRowHeight
        };

        if (getRowSpacing) {
          var _spacing$top, _spacing$bottom;

          const indexRelativeToCurrentPage = apiRef.current.getRowIndexRelativeToVisibleRows(row.id);
          const spacing = getRowSpacing(_extends({}, row, {
            isFirstVisible: indexRelativeToCurrentPage === 0,
            isLastVisible: indexRelativeToCurrentPage === currentPage.rows.length - 1,
            indexRelativeToCurrentPage
          }));
          initialHeights.spacingTop = (_spacing$top = spacing.top) != null ? _spacing$top : 0;
          initialHeights.spacingBottom = (_spacing$bottom = spacing.bottom) != null ? _spacing$bottom : 0;
        }

        const processedSizes = apiRef.current.unstable_applyPipeProcessors('rowHeight', initialHeights, row);
        rowsHeightLookup.current[row.id].sizes = processedSizes;
        const finalRowHeight = Object.values(processedSizes).reduce((acc2, value) => acc2 + value, 0);
        return acc + finalRowHeight;
      }, 0);
      return _extends({}, state, {
        rowsMeta: {
          currentPageTotalHeight,
          positions
        }
      });
    });

    if (!hasRowWithAutoHeight.current) {
      // No row has height=auto, so all rows are already measured
      lastMeasuredRowIndex.current = Infinity;
    }

    apiRef.current.forceUpdate();
  }, [apiRef, currentPage.rows, rowHeightFromDensity, getRowHeightProp, getRowSpacing, getEstimatedRowHeight]);
  const getRowHeight = React.useCallback(rowId => {
    const height = rowsHeightLookup.current[rowId];
    return height ? height.sizes.base : rowHeightFromDensity;
  }, [rowHeightFromDensity]);

  const getRowInternalSizes = rowId => {
    var _rowsHeightLookup$cur;

    return (_rowsHeightLookup$cur = rowsHeightLookup.current[rowId]) == null ? void 0 : _rowsHeightLookup$cur.sizes;
  };

  const setRowHeight = React.useCallback((id, height) => {
    rowsHeightLookup.current[id].sizes.base = height;
    rowsHeightLookup.current[id].isResized = true;
    rowsHeightLookup.current[id].needsFirstMeasurement = false;
    hydrateRowsMeta();
  }, [hydrateRowsMeta]);
  const storeMeasuredRowHeight = React.useCallback((id, height) => {
    if (!rowsHeightLookup.current[id] || !rowsHeightLookup.current[id].autoHeight) {
      return;
    } // Only trigger hydration if the value is different, otherwise we trigger a loop


    const needsHydration = rowsHeightLookup.current[id].sizes.base !== height;
    rowsHeightLookup.current[id].needsFirstMeasurement = false;
    rowsHeightLookup.current[id].sizes.base = height;

    if (needsHydration) {
      hydrateRowsMeta();
    }
  }, [hydrateRowsMeta]);
  const rowHasAutoHeight = React.useCallback(id => {
    var _rowsHeightLookup$cur2;

    return ((_rowsHeightLookup$cur2 = rowsHeightLookup.current[id]) == null ? void 0 : _rowsHeightLookup$cur2.autoHeight) || false;
  }, []);
  const getLastMeasuredRowIndex = React.useCallback(() => {
    return lastMeasuredRowIndex.current;
  }, []);
  const setLastMeasuredRowIndex = React.useCallback(index => {
    if (hasRowWithAutoHeight.current) {
      lastMeasuredRowIndex.current = index;
    }
  }, []); // The effect is used to build the rows meta data - currentPageTotalHeight and positions.
  // Because of variable row height this is needed for the virtualization

  React.useEffect(() => {
    hydrateRowsMeta();
  }, [rowHeightFromDensity, filterState, paginationState, sortingState, hydrateRowsMeta]);
  useGridRegisterPipeApplier(apiRef, 'rowHeight', hydrateRowsMeta);
  const rowsMetaApi = {
    unstable_getLastMeasuredRowIndex: getLastMeasuredRowIndex,
    unstable_setLastMeasuredRowIndex: setLastMeasuredRowIndex,
    unstable_rowHasAutoHeight: rowHasAutoHeight,
    unstable_getRowHeight: getRowHeight,
    unstable_getRowInternalSizes: getRowInternalSizes,
    unstable_setRowHeight: setRowHeight,
    unstable_storeRowHeightMeasurement: storeMeasuredRowHeight
  };
  useGridApiMethod(apiRef, rowsMetaApi, 'GridRowsMetaApi');
};